import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Logo } from "../components/brand/Logo";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const field = "w-full px-4 py-3 rounded-xl border border-sambiva-charcoal/15 bg-white focus:outline-none focus:ring-2 focus:ring-sambiva-red/40 text-sm";
const Shell = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-sambiva-cream p-6">
    <div className="w-full max-w-sm bg-white rounded-3xl p-8 border border-sambiva-charcoal/5">
      <Link to="/"><Logo className="h-10 mb-6 mx-auto" /></Link>
      {children}
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => { document.title = "Forgot Password | Sambiva"; }, []);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Shell>
      {sent ? (
        <div className="text-center" data-testid="forgot-sent">
          <CheckCircle2 className="mx-auto text-sambiva-green mb-3" size={40} />
          <h1 className="font-serif text-2xl text-sambiva-charcoal mb-2">Check your email</h1>
          <p className="text-sambiva-muted text-sm">If an account exists, we've sent a reset link.</p>
        </div>
      ) : (
        <>
          <h1 className="font-serif text-2xl text-sambiva-charcoal mb-2">Reset your password</h1>
          <p className="text-sambiva-muted text-sm mb-6">Enter your email and we'll send a reset link.</p>
          <form onSubmit={submit} className="space-y-4">
            <input data-testid="forgot-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
            <button data-testid="forgot-submit" disabled={loading} className="w-full bg-sambiva-red text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="animate-spin" size={18} />} Send reset link
            </button>
          </form>
          <p className="text-sm text-center mt-5"><Link to="/trade/login" className="text-sambiva-red hover:underline">Back to login</Link></p>
        </>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { document.title = "Set New Password | Sambiva"; }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in.");
      navigate("/trade/login");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };
  return (
    <Shell>
      <h1 className="font-serif text-2xl text-sambiva-charcoal mb-2">Set a new password</h1>
      <p className="text-sambiva-muted text-sm mb-6">Choose a strong password (min 8 characters).</p>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="reset-password" type="password" required placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} />
        <input data-testid="reset-confirm" type="password" required placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={field} />
        <button data-testid="reset-submit" disabled={loading} className="w-full bg-sambiva-red text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
          {loading && <Loader2 className="animate-spin" size={18} />} Update password
        </button>
      </form>
    </Shell>
  );
}

export function ActivateAccount() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { document.title = "Activate Account | Sambiva"; }, []);
  useEffect(() => {
    api.get(`/auth/verify-invitation?token=${token}`).then((r) => setInfo(r.data)).catch(() => setInvalid(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Account activated — please sign in.");
      navigate("/trade/login");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  return (
    <Shell>
      {invalid ? (
        <div className="text-center"><h1 className="font-serif text-2xl text-sambiva-charcoal mb-2">Link expired</h1><p className="text-sambiva-muted text-sm">This invitation link is invalid or has expired.</p></div>
      ) : !info ? (
        <div className="text-center py-6"><Loader2 className="animate-spin mx-auto text-sambiva-red" size={28} /></div>
      ) : (
        <>
          <h1 className="font-serif text-2xl text-sambiva-charcoal mb-2">Welcome, {info.name}</h1>
          <p className="text-sambiva-muted text-sm mb-6">Set your password to activate your Sambiva trade account.</p>
          <form onSubmit={submit} className="space-y-4">
            <input value={info.email} disabled className={`${field} bg-sambiva-cream`} />
            <input data-testid="activate-password" type="password" required placeholder="Create password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} />
            <input data-testid="activate-confirm" type="password" required placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={field} />
            <button data-testid="activate-submit" disabled={loading} className="w-full bg-sambiva-green text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="animate-spin" size={18} />} Activate account
            </button>
          </form>
        </>
      )}
    </Shell>
  );
}
